import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

import AuthService from "../services/auth.service";
import CartService from "../services/cart.service";
import toast from "react-hot-toast";
import i18n from "../i18n";

// Global cart state: holds the serialized cart from the server and exposes
// helpers used by the navbar badge, catalog "Add to cart", and the Cart page.
const CartContext = createContext(null);

const EMPTY = { cart_id: null, items: [], item_count: 0, total_amount: 0 };

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const isLoggedIn = () => !!AuthService.getCurrentUser();

  const refresh = useCallback(() => {
    if (!isLoggedIn()) {
      setCart(EMPTY);
      return Promise.resolve(EMPTY);
    }
    setLoading(true);
    return CartService.get()
      .then((res) => {
        setCart(res.data);
        return res.data;
      })
      .catch(() => EMPTY)
      .finally(() => setLoading(false));
  }, []);

  // Load the cart on mount (if logged in) and whenever auth changes in another tab.
  useEffect(() => {
    refresh();
    const onStorage = (e) => {
      if (e.key === "user") refresh();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [refresh]);

  const resolveMsg = (err) =>
    (err.response && err.response.data && err.response.data.message) ||
    err.message ||
    i18n.t("common.somethingWrong");

  const addToCart = useCallback(
    (productId, quantity = 1) => {
      if (!isLoggedIn()) {
        toast.error(i18n.t("cart.signInToAdd"));
        return Promise.reject(new Error("not-authenticated"));
      }
      return CartService.addItem(productId, quantity)
        .then((res) => {
          setCart(res.data);
          toast.success(i18n.t("cart.addedToCart"));
          return res.data;
        })
        .catch((err) => {
          toast.error(resolveMsg(err));
          throw err;
        });
    },
    []
  );

  const updateQuantity = useCallback((itemId, quantity) => {
    return CartService.updateItem(itemId, quantity)
      .then((res) => {
        setCart(res.data);
        return res.data;
      })
      .catch((err) => {
        toast.error(resolveMsg(err));
        throw err;
      });
  }, []);

  const removeItem = useCallback((itemId) => {
    return CartService.removeItem(itemId)
      .then((res) => {
        setCart(res.data);
        return res.data;
      })
      .catch((err) => {
        toast.error(resolveMsg(err));
        throw err;
      });
  }, []);

  const clearCart = useCallback(() => {
    return CartService.clear()
      .then((res) => {
        setCart(res.data);
        return res.data;
      })
      .catch((err) => {
        toast.error(resolveMsg(err));
        throw err;
      });
  }, []);

  // Call after a successful checkout (server already emptied the cart).
  const resetCart = useCallback(() => setCart(EMPTY), []);

  const value = {
    cart,
    loading,
    refresh,
    addToCart,
    updateQuantity,
    removeItem,
    clearCart,
    resetCart,
    itemCount: cart.item_count || 0,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within a CartProvider");
  return ctx;
};

export default CartContext;
